import { useState } from "react";
import { Button } from "@/components/ui/button";
import { QrCode } from "lucide-react";
import QrScanner from "@/components/qr/QrScanner";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface Props {
  bookingId: string;
  spotId: string;
  checkedIn: boolean;
  checkedOut: boolean;
  onUpdated?: () => void;
}

const CheckInOutButton = ({ bookingId, spotId, checkedIn, checkedOut, onUpdated }: Props) => {
  const [open, setOpen] = useState(false);
  const { toast } = useToast();

  if (checkedOut) return null;

  const label = checkedIn ? "Scan to Check-Out" : "Scan to Check-In";

  const handleScanned = async (token: string) => {
    const { data: spot } = await supabase
      .from("parking_spots")
      .select("qr_token")
      .eq("id", spotId)
      .maybeSingle();
    if (!spot || spot.qr_token !== token) {
      toast({ title: "Wrong QR", description: "This code does not match your booked spot.", variant: "destructive" });
      return;
    }
    const field = checkedIn ? "checked_out_at" : "checked_in_at";
    const { error } = await supabase
      .from("bookings")
      .update({ [field]: new Date().toISOString() })
      .eq("id", bookingId);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: checkedIn ? "Checked out" : "Checked in" });
    onUpdated?.();
  };

  return (
    <>
      <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); setOpen(true); }}>
        <QrCode className="h-4 w-4 mr-1" /> {label}
      </Button>
      <QrScanner open={open} onOpenChange={setOpen} onScanned={handleScanned} />
    </>
  );
};

export default CheckInOutButton;
